import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ClipboardCopy, ListChecks } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { AIInsightsUnanswered } from '@/types/ai'

// F-AI-55: the quality loop on the AI developer dashboard. Unanswered
// questions with a triage label each (kb / tool / nav / wontfix), the latest
// thumbs-downs, the weekly reports, and "copy eval cases" for the triaged
// ones. Reads GET /ai/insights, which 404s unless AI_INSIGHTS_ENABLED=1 and
// the signed-in user is a platform admin — the panel then shows nothing.

const LABELS: { value: AIInsightsUnanswered['triage']; text: string }[] = [
  { value: '', text: '—' },
  { value: 'kb', text: 'kb: write an article' },
  { value: 'tool', text: 'tool: needs data' },
  { value: 'nav', text: 'nav: needs a target' },
  { value: 'wontfix', text: 'wontfix' },
]

export function QualityPanel() {
  const qc = useQueryClient()
  const [days, setDays] = useState(7)
  const insights = useQuery({
    queryKey: ['ai-insights', days],
    queryFn: () => apiClient.aiGetInsights(days),
    retry: false,
    staleTime: 30_000,
  })
  const reports = useQuery({ queryKey: ['ai-quality-reports'], queryFn: () => apiClient.aiQualityReports(6), retry: false, staleTime: 60_000 })
  const triage = useMutation({
    mutationFn: (body: { query: string; signal: string; label: string }) => apiClient.aiTriageMiss(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-insights'] }),
  })
  const [copied, setCopied] = useState<string | null>(null)
  const copyCases = async () => {
    const r = await apiClient.aiExportEvalCases()
    const cases = r.data?.cases ?? []
    try {
      await navigator.clipboard.writeText(JSON.stringify(cases, null, 2))
      setCopied(`${cases.length} case${cases.length === 1 ? '' : 's'} copied`)
    } catch {
      setCopied('Could not copy')
    }
    window.setTimeout(() => setCopied(null), 2500)
  }

  if (insights.isError) return null
  const d = insights.data?.data

  return (
    <Card className="min-w-0">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4 text-primary" aria-hidden /> Quality loop
          </CardTitle>
          <div className="ml-auto flex items-center gap-1">
            {[7, 30].map((n) => (
              <Button key={n} type="button" size="sm" variant={days === n ? 'default' : 'outline'} onClick={() => setDays(n)}>
                {n}d
              </Button>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => void copyCases()}>
              <ClipboardCopy className="mr-1.5 h-4 w-4" aria-hidden /> Copy eval cases
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Unanswered questions, newest window first. Label each one; kb / tool / nav items export as eval cases.
          {copied && <span className="ml-2 text-foreground">{copied}</span>}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">×</th>
              <th className="px-2 py-1.5 font-medium">Question</th>
              <th className="px-2 py-1.5 font-medium">Signal</th>
              <th className="px-2 py-1.5 font-medium">Triage</th>
            </tr>
          </thead>
          <tbody>
            {(d?.top_unanswered ?? []).map((u) => (
              <tr key={`${u.query}|${u.signal}`} className="border-b border-border/60">
                <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{u.count}</td>
                <td className="px-2 py-1.5">{u.query}</td>
                <td className="px-2 py-1.5">
                  <Badge variant="secondary">{u.signal}</Badge>
                </td>
                <td className="px-2 py-1.5">
                  <select
                    aria-label={`Triage: ${u.query}`}
                    value={u.triage}
                    disabled={triage.isPending}
                    onChange={(e) => triage.mutate({ query: u.query, signal: u.signal, label: e.target.value })}
                    className={cn('rounded-md border border-input bg-background px-2 py-1 text-xs', u.triage && 'font-medium')}
                  >
                    {LABELS.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.text}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {d && d.top_unanswered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                  Nothing unanswered in the last {days} days.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!!d?.thumbs_down?.length && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Latest thumbs-down replies</p>
            <ul className="space-y-1 text-sm">
              {d.thumbs_down.slice(0, 5).map((t, i) => (
                <li key={i} className="rounded-md border border-border px-3 py-1.5">
                  <span className="line-clamp-2">{t.reply}</span>
                  {t.note && <span className="block text-xs text-muted-foreground">note: {t.note}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Weekly reports</p>
          {reports.data?.data?.length ? (
            <ul className="space-y-1 text-sm">
              {reports.data.data.map((r) => (
                <li key={r.week_start}>
                  <details className="rounded-md border border-border px-3 py-1.5">
                    <summary className="cursor-pointer">
                      Week of {r.week_start} <span className="text-xs text-muted-foreground">· {r.delivered_via || 'stored only'}</span>
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap text-xs">{r.content}</pre>
                  </details>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">No report yet. The first goes out Monday 09:00 once AI_QUALITY_DIGEST_EMAIL is set.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
